// BEAD Reimbursement (Draw Packet) — interactive page for building, reviewing,
// and exporting a draw claim against a project's BEAD budget. Lives under
// Compliance. /bead-compliance/reimbursement/:projectId  (or no id → picker)

import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useParams, useNavigate } from 'react-router-dom';
import { api } from '../lib/api';

const CATEGORY_DISPLAY_TO_CODE: Record<string, string> = {
  'Aerial Fiber Construction': 'aerial_fiber',
  'Aerial Fiber':              'aerial_fiber',
  'Buried Fiber Construction': 'buried_fiber',
  'Buried Fiber':              'buried_fiber',
  'Network Equipment':         'network_equipment',
  'Drops & CPE':               'drops_cpe',
  'Engineering':               'engineering',
  'Engineering & Design':      'engineering',
  'Permits/ROW':               'permits_row',
  'Permits / ROW':             'permits_row',
  'PM':                        'project_mgmt',
  'Project Management':        'project_mgmt',
  'Contingency':               'contingency',
};

type QboAccount = {
  id: string;
  qbo_account_code: string;
  qbo_account_name: string;
  bead_category: string;
  ytd_balance_usd: number | string;
};


type Project = {
  id: string;
  project_code: string;
  project_name: string;
  county: string;
  state: string;
  awarded_bsl_count: number;
  total_budget_usd: string | null;
};

type DrawData = {
  project: {
    id: string; project_code: string; project_name: string; county: string;
    state: string; tech_type: string; awarded_bsl_count: number;
    grant_amount_usd: number; match_amount_usd: number; total_project_cost_usd: number;
    federal_share_pct: number;
  };
  period: { start: string; end: string; progress_pct: number };
  categories: {
    category: string; budget: number; actual: number;
    claimed_federal: number; drawn_match: number; federal_share_pct: number;
    variance_pct: number; variance_flag: 'ok'|'over'|'under';
    line_items: number; synthesized: boolean;
  }[];
  totals: { budget: number; actual: number; claimed_federal: number; drawn_match: number };
};

const fmtUSD = (n: number) => {
  if (Math.abs(n) >= 1e6) return `$${(n / 1e6).toFixed(2)}M`;
  if (Math.abs(n) >= 1e3) return `$${(n / 1e3).toFixed(0)}K`;
  return `$${Math.round(n).toLocaleString()}`;
};

export default function ReimbursementPage() {
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const params = useParams();
  const navigate = useNavigate();
  const projectId = params.projectId;

  // Picker mode: no project selected
  const { data: projects = [] } = useQuery<Project[]>({
    queryKey: ['bead-projects'],
    queryFn: () => api.get('/bead/projects'),
  });

  // Period + progress controls
  const [periodStart, setPeriodStart] = useState('2026-04-01');
  const [periodEnd,   setPeriodEnd]   = useState('2026-04-30');
  const [progressPct, setProgressPct] = useState(30);
  // Debounced shadows — these drive the query, not the inputs. The inputs
  // update live (smooth slider drag); the query waits 250ms after the last
  // change to refetch.
  const [dProgress, setDProgress] = useState(30);
  const [dStart,    setDStart]    = useState('2026-04-01');
  const [dEnd,      setDEnd]      = useState('2026-04-30');

  useEffect(() => {
    const t = setTimeout(() => {
      setDProgress(progressPct);
      setDStart(periodStart);
      setDEnd(periodEnd);
    }, 250);
    return () => clearTimeout(t);
  }, [progressPct, periodStart, periodEnd]);

  // Reset slider + period when the user navigates to a different project.
  // (React Router keeps the component mounted, so useState would otherwise
  // bleed between projects.)
  useEffect(() => {
    setProgressPct(30);
    setPeriodStart('2026-04-01');
    setPeriodEnd('2026-04-30');
    setDProgress(30);
    setDStart('2026-04-01');
    setDEnd('2026-04-30');
  }, [projectId]);

  const { data: qboAccounts = [] } = useQuery<QboAccount[]>({
    queryKey: ['qbo-accounts'],
    queryFn: () => api.get('/bead-pm/qbo-accounts'),
  });

  const { data: draw, isLoading } = useQuery<DrawData>({
    queryKey: ['reimbursement', projectId, dStart, dEnd, dProgress],
    queryFn: () => api.get(`/bead/projects/${projectId}/reimbursement?period_start=${dStart}&period_end=${dEnd}&progress_pct=${dProgress}`),
    enabled: !!projectId,
    placeholderData: (prev: any) => prev,
  });

  // Project picker view
  if (!projectId) {
    const byState: Record<string, Project[]> = {};
    for (const p of projects) {
      const st = (p.state ?? 'Other').toUpperCase();
      (byState[st] = byState[st] || []).push(p);
    }
    const order = ['WY','CO','NE','TX','Other'].filter(s => byState[s]?.length);
    return (
      <div style={{ padding: 24, maxWidth: 1100, margin: '0 auto' }}>
        <h1 style={{ margin: 0, fontSize: 22 }}>BEAD Reimbursement</h1>
        <p style={{ color: '#64748b', fontSize: 13, marginTop: 4 }}>
          Select a project to start a draw packet. Each draw reconciles your BoM × catalog (live), QuickBooks-imported invoices (via Milestone Core), and category-level overrides into the SBO's required claim format.
        </p>
        <div style={{ marginTop: 20 }}>
          {order.map(st => (
            <div key={st} style={{ marginBottom: 18 }}>
              <h3 style={{ margin: '0 0 8px 0', fontSize: 12, textTransform: 'uppercase', letterSpacing: '.06em', color: '#475569' }}>{st}</h3>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(360px, 1fr))', gap: 10 }}>
                {byState[st].map(p => (
                  <button
                    key={p.id}
                    onClick={() => navigate(`/bead-compliance/reimbursement/${p.id}`)}
                    style={{
                      textAlign: 'left', padding: 12, border: '1px solid #e2e8f0',
                      borderRadius: 6, background: '#fff', cursor: 'pointer',
                    }}>
                    <div style={{ fontWeight: 600, fontSize: 13 }}>{p.project_code}</div>
                    <div style={{ fontSize: 12, color: '#475569', marginTop: 2 }}>{p.project_name}</div>
                    <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 4 }}>
                      {p.county} County · {Number(p.awarded_bsl_count || 0).toLocaleString()} BSLs · {p.total_budget_usd ? fmtUSD(Number(p.total_budget_usd)) : '—'}
                    </div>
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (isLoading || !draw) {
    return <div style={{ padding: 24, color: '#64748b' }}>Loading draw…</div>;
  }

  const fed_pct = (draw.project.federal_share_pct * 100).toFixed(1);
  const overTotal  = draw.categories.filter(c => c.variance_flag === 'over').length;
  const underTotal = draw.categories.filter(c => c.variance_flag === 'under').length;

  // PDF link with all params
  const packetUrl = `/bead-compliance/reimbursement-packet/${projectId}?period_start=${periodStart}&period_end=${periodEnd}&progress_pct=${progressPct}`;

  return (
    <div style={{ padding: 24, maxWidth: 1200, margin: '0 auto' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 12 }}>
        <div>
          <a onClick={() => navigate('/bead-compliance/reimbursement')} style={{ fontSize: 11, color: '#64748b', cursor: 'pointer' }}>← All projects</a>
          <h1 style={{ margin: '4px 0 0 0', fontSize: 22 }}>{draw.project.project_code}</h1>
          <div style={{ fontSize: 13, color: '#475569', marginTop: 2 }}>
            {draw.project.project_name} · {draw.project.county} County, {draw.project.state} · {Number(draw.project.awarded_bsl_count).toLocaleString()} BSLs · {draw.project.tech_type}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <a href={packetUrl} target="_blank" rel="noreferrer" className="btn btn-primary"
             style={{ background: '#0f172a', color: '#fff', padding: '8px 16px', borderRadius: 4, textDecoration: 'none', fontSize: 13, fontWeight: 600 }}>
            Generate Draw Packet ↗
          </a>
        </div>
      </div>

      {/* Controls + summary */}
      <div style={{ marginTop: 18, padding: 14, background: '#f8fafc', borderRadius: 6, display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 16, alignItems: 'end' }}>
        <div>
          <label style={{ fontSize: 10, color: '#64748b', display: 'block', textTransform: 'uppercase', letterSpacing: '.06em' }}>Period start</label>
          <input type="date" value={periodStart} onChange={e => setPeriodStart(e.target.value)} style={{ width: '100%', padding: 6, border: '1px solid #cbd5e1', borderRadius: 4, fontSize: 12 }} />
        </div>
        <div>
          <label style={{ fontSize: 10, color: '#64748b', display: 'block', textTransform: 'uppercase', letterSpacing: '.06em' }}>Period end</label>
          <input type="date" value={periodEnd} onChange={e => setPeriodEnd(e.target.value)} style={{ width: '100%', padding: 6, border: '1px solid #cbd5e1', borderRadius: 4, fontSize: 12 }} />
        </div>
        <div>
          <label style={{ fontSize: 10, color: '#64748b', display: 'block', textTransform: 'uppercase', letterSpacing: '.06em' }}>Build progress {progressPct}%</label>
          <input type="range" min={0} max={100} value={progressPct} onChange={e => setProgressPct(Number(e.target.value))} style={{ width: '100%' }} />
        </div>
        <div>
          <label style={{ fontSize: 10, color: '#64748b', display: 'block', textTransform: 'uppercase', letterSpacing: '.06em' }}>Federal share</label>
          <div style={{ fontSize: 13, fontWeight: 600 }}>{fed_pct}%</div>
        </div>
      </div>

      {/* Hero numbers */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginTop: 14 }}>
        <Hero label="Project budget" value={fmtUSD(draw.project.total_project_cost_usd)} sub={`${fmtUSD(draw.project.grant_amount_usd)} federal · ${fmtUSD(draw.project.match_amount_usd)} match`} />
        <Hero label="Period actuals" value={fmtUSD(draw.totals.actual)} sub={`${Math.round(draw.totals.actual / draw.project.total_project_cost_usd * 100)}% of total budget`} />
        <Hero label="Federal claim" value={fmtUSD(draw.totals.claimed_federal)} sub="Reimbursable from SBO" accent="#10b981" />
        <Hero label="Match drawn" value={fmtUSD(draw.totals.drawn_match)} sub="Out-of-pocket this period" accent="#f59e0b" />
      </div>

      {(overTotal + underTotal > 0) && (
        <div style={{ marginTop: 12, padding: '8px 12px', background: '#fef3c7', border: '1px solid #fde68a', borderRadius: 4, fontSize: 12, color: '#92400e' }}>
          ⚠ {overTotal > 0 && <span>{overTotal} categor{overTotal === 1 ? 'y' : 'ies'} over the build-pace expectation</span>}
          {overTotal > 0 && underTotal > 0 && ', '}
          {underTotal > 0 && <span>{underTotal} categor{underTotal === 1 ? 'y' : 'ies'} under-actuals</span>}
          {' — review before submitting.'}
        </div>
      )}

      {/* Category table */}
      <div style={{ marginTop: 18, border: '1px solid #e2e8f0', borderRadius: 6, overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ background: '#f1f5f9' }}>
              <th style={{ textAlign: 'left',  padding: '8px 10px', fontWeight: 700, fontSize: 11, textTransform: 'uppercase', letterSpacing: '.05em', color: '#475569' }}>Category</th>
              <th style={{ textAlign: 'right', padding: '8px 10px', fontWeight: 700, fontSize: 11, textTransform: 'uppercase', letterSpacing: '.05em', color: '#475569' }}>Budget</th>
              <th style={{ textAlign: 'right', padding: '8px 10px', fontWeight: 700, fontSize: 11, textTransform: 'uppercase', letterSpacing: '.05em', color: '#475569' }}>Actual (period)</th>
              <th style={{ textAlign: 'right', padding: '8px 10px', fontWeight: 700, fontSize: 11, textTransform: 'uppercase', letterSpacing: '.05em', color: '#10b981' }}>Federal claim</th>
              <th style={{ textAlign: 'right', padding: '8px 10px', fontWeight: 700, fontSize: 11, textTransform: 'uppercase', letterSpacing: '.05em', color: '#f59e0b' }}>Match drawn</th>
              <th style={{ textAlign: 'center', padding: '8px 10px', fontWeight: 700, fontSize: 11, textTransform: 'uppercase', letterSpacing: '.05em', color: '#475569' }}>Variance</th>
            </tr>
          </thead>
          <tbody>
            {draw.categories.map((c, i) => (
              <tr key={c.category}
                onClick={() => setSelectedCategory(c.category)}
                style={{
                  borderTop: i > 0 ? '1px solid #e2e8f0' : 'none',
                  background: c.variance_flag === 'over' ? '#fef3c7' : '#fff',
                  cursor: 'pointer',
                }}
                onMouseEnter={(e) => { e.currentTarget.style.background = c.variance_flag === 'over' ? '#fef3c7' : '#f8fafc'; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = c.variance_flag === 'over' ? '#fef3c7' : '#fff'; }}>
                <td style={{ padding: '8px 10px' }}>
                  <div style={{ fontWeight: 600 }}>{c.category}</div>
                  <div style={{ fontSize: 10, color: '#94a3b8', marginTop: 2 }}>
                    {c.synthesized ? 'Synthesized from project total' : `${c.line_items} line item${c.line_items === 1 ? '' : 's'}`}
                  </div>
                </td>
                <td style={{ padding: '8px 10px', textAlign: 'right' }}>{fmtUSD(c.budget)}</td>
                <td style={{ padding: '8px 10px', textAlign: 'right', fontWeight: 600 }}>{fmtUSD(c.actual)}</td>
                <td style={{ padding: '8px 10px', textAlign: 'right', color: '#10b981', fontWeight: 700 }}>{fmtUSD(c.claimed_federal)}</td>
                <td style={{ padding: '8px 10px', textAlign: 'right', color: '#f59e0b' }}>{fmtUSD(c.drawn_match)}</td>
                <td style={{ padding: '8px 10px', textAlign: 'center' }}>
                  <span style={{
                    fontSize: 11, padding: '2px 6px', borderRadius: 3, fontWeight: 600,
                    background: c.variance_flag === 'over' ? '#fee2e2' : c.variance_flag === 'under' ? '#fef3c7' : '#dcfce7',
                    color:      c.variance_flag === 'over' ? '#b91c1c' : c.variance_flag === 'under' ? '#92400e' : '#047857',
                  }}>
                    {c.variance_pct > 0 ? '+' : ''}{c.variance_pct}%
                  </span>
                </td>
              </tr>
            ))}
            <tr style={{ borderTop: '2px solid #1e293b', background: '#f8fafc', fontWeight: 700 }}>
              <td style={{ padding: '10px' }}>TOTAL</td>
              <td style={{ padding: '10px', textAlign: 'right' }}>{fmtUSD(draw.totals.budget)}</td>
              <td style={{ padding: '10px', textAlign: 'right' }}>{fmtUSD(draw.totals.actual)}</td>
              <td style={{ padding: '10px', textAlign: 'right', color: '#10b981' }}>{fmtUSD(draw.totals.claimed_federal)}</td>
              <td style={{ padding: '10px', textAlign: 'right', color: '#f59e0b' }}>{fmtUSD(draw.totals.drawn_match)}</td>
              <td></td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* Data-source note */}
      <div style={{ marginTop: 16, fontSize: 11, color: '#64748b', maxWidth: 800 }}>
        <strong style={{ color: '#334155' }}>Actuals sources:</strong> BoM consumed × catalog price (live, from this project's plant graph) ·
        Crew labor hours × work order rates (live) ·
        Accounting-system actuals (subcontractor invoices, permits, ROW fees, engineering vendor bills) — currently synthesized at the build-progress slider value above; production deployment imports these from QuickBooks Online via Milestone Core's QBO integration.
      </div>

      {selectedCategory && (() => {
        const code = CATEGORY_DISPLAY_TO_CODE[selectedCategory] ?? '';
        const matched = qboAccounts.filter((a: QboAccount) => a.bead_category === code);
        const ytdSum = matched.reduce((sum: number, a: QboAccount) => sum + (Number(a.ytd_balance_usd) || 0), 0);
        return (
          <div onClick={() => setSelectedCategory(null)}
            style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.4)', zIndex: 100, display: 'flex', justifyContent: 'flex-end' }}>
            <div onClick={e => e.stopPropagation()}
              style={{ width: 520, maxWidth: '100%', background: 'white', height: '100vh', overflowY: 'auto', padding: 24, boxShadow: '-4px 0 24px rgba(0,0,0,0.15)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
                <div>
                  <div style={{ fontSize: 11, color: '#64748b', textTransform: 'uppercase', letterSpacing: '.05em', fontWeight: 700 }}>BEAD Category</div>
                  <div style={{ fontSize: 18, fontWeight: 700, marginTop: 4 }}>{selectedCategory}</div>
                  <div style={{ fontSize: 12, color: '#64748b', marginTop: 4 }}>{matched.length} QBO account{matched.length === 1 ? '' : 's'} mapped \u00b7 {fmtUSD(ytdSum)} YTD</div>
                </div>
                <button onClick={() => setSelectedCategory(null)}
                  style={{ fontSize: 24, color: '#64748b', background: 'transparent', border: 0, cursor: 'pointer', lineHeight: 1 }}>\u00d7</button>
              </div>
              <div style={{ marginTop: 20 }}>
                <div style={{ fontSize: 10, color: '#64748b', textTransform: 'uppercase', letterSpacing: '.05em', fontWeight: 700, marginBottom: 8 }}>
                  Mapped QBO Accounts
                </div>
                {matched.length === 0 ? (
                  <div style={{ padding: 16, fontSize: 13, color: '#64748b', fontStyle: 'italic', textAlign: 'center', border: '1px dashed #e2e8f0', borderRadius: 4 }}>
                    No QBO accounts mapped to this category yet.
                  </div>
                ) : (
                  matched.map((a: QboAccount) => (
                    <div key={a.id}
                      style={{ padding: '10px 12px', borderBottom: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', gap: 10 }}>
                      <div style={{ width: 50, fontFamily: 'ui-monospace, monospace', fontSize: 11, color: '#94a3b8' }}>{a.qbo_account_code}</div>
                      <div style={{ flex: 1, fontSize: 13 }}>{a.qbo_account_name}</div>
                      <div style={{ fontSize: 12, color: '#475569', fontVariantNumeric: 'tabular-nums' }}>
                        {Number(a.ytd_balance_usd) > 0 ? fmtUSD(Number(a.ytd_balance_usd)) : '\u2014'}
                      </div>
                    </div>
                  ))
                )}
              </div>
              <div style={{ marginTop: 16, padding: 12, background: '#f8fafc', borderRadius: 4, fontSize: 12, color: '#64748b', lineHeight: 1.5 }}>
                Actuals on this row come from these QBO accounts. Adjust the mapping in
                {' '}<a href="/settings/qbo-mapping" style={{ color: '#3b82f6', fontWeight: 600 }}>Settings \u2192 QBO Mapping</a>.
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
}

function Hero({ label, value, sub, accent }: { label: string; value: string; sub: string; accent?: string }) {
  return (
    <div style={{ padding: 12, background: '#fff', border: '1px solid #e2e8f0', borderRadius: 6 }}>
      <div style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: '.06em', color: '#64748b' }}>{label}</div>
      <div style={{ fontSize: 22, fontWeight: 700, marginTop: 4, color: accent ?? '#0f172a' }}>{value}</div>
      <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 2 }}>{sub}</div>
    </div>
  );
}
